/**
 * The frame of a cut (D44) as shares of the original, 0 to 1, like `schemas/crop.py`: the
 * dialog measures the picture as the browser shows it, the server cuts the stored file, and
 * the two agree on shares. Pure arithmetic; the dialog only feeds it pointer and key moves.
 */
/** The whole picture: "Ganzes Bild". */
export const WHOLE = { x: 0, y: 0, w: 1, h: 1 };
/** The smallest side a frame keeps, so a slip of the pointer cannot make it vanish. */
export const MIN_SHARE = 0.02;
export const HANDLES = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];
export const CROP_RATIOS = ["free", "16:9", "4:3", "1:1"];
const RATIO_VALUES = {
    "16:9": 16 / 9,
    "4:3": 4 / 3,
    "1:1": 1,
};
/**
 * The proportion of a frame in shares, for a proportion in pixels: a square on a picture
 * twice as wide as high is half its width and its whole height. `null` for "free".
 */
export function shareAspect(ratio, pictureAspect) {
    return ratio === "free" || pictureAspect <= 0 ? null : RATIO_VALUES[ratio] / pictureAspect;
}
/** The largest frame of a proportion, in the middle of the picture; the whole one when free. */
export function fitted(aspect) {
    if (aspect === null) {
        return WHOLE;
    }
    const w = Math.min(1, aspect);
    const h = w / aspect;
    return { x: (1 - w) / 2, y: (1 - h) / 2, w, h };
}
function clamp(value, low, high) {
    return Math.min(high, Math.max(low, value));
}
/** The frame moved by a share of the picture, held inside it. */
export function moveFrame(frame, dx, dy) {
    return {
        ...frame,
        x: clamp(frame.x + dx, 0, 1 - frame.w),
        y: clamp(frame.y + dy, 0, 1 - frame.h),
    };
}
/**
 * The frame with one grip pulled by a share of the picture. The opposite edge stays where it
 * is. With a proportion, the width leads (the height follows it), except on the top and bottom
 * edge; a frame that would leave the picture shrinks until it fits.
 */
export function resizeFrame(frame, handle, dx, dy, aspect) {
    const west = handle.includes("w");
    const north = handle.includes("n");
    let left = frame.x;
    let top = frame.y;
    let right = frame.x + frame.w;
    let bottom = frame.y + frame.h;
    if (west)
        left = clamp(left + dx, 0, right - MIN_SHARE);
    if (handle.includes("e"))
        right = clamp(right + dx, left + MIN_SHARE, 1);
    if (north)
        top = clamp(top + dy, 0, bottom - MIN_SHARE);
    if (handle.includes("s"))
        bottom = clamp(bottom + dy, top + MIN_SHARE, 1);
    if (aspect === null) {
        return { x: left, y: top, w: right - left, h: bottom - top };
    }
    let w = right - left;
    let h = bottom - top;
    if (handle === "n" || handle === "s") {
        w = h * aspect;
    }
    else {
        h = w / aspect;
    }
    const room = Math.min(1, (west ? right : 1 - left) / w, (north ? bottom : 1 - top) / h);
    w *= room;
    h *= room;
    return { x: west ? right - w : left, y: north ? bottom - h : top, w, h };
}
/** Four decimals, as the server stores them; enough for any picture a door shows. */
export function rounded(frame) {
    const four = (value) => Math.round(value * 10_000) / 10_000;
    const x = four(frame.x);
    const y = four(frame.y);
    return { x, y, w: Math.min(four(frame.w), 1 - x), h: Math.min(four(frame.h), 1 - y) };
}
//# sourceMappingURL=crop.js.map